<template>
  <div
    v-if="participantVisibilityMode !== 'hidden'"
    class="join-community px-4"
  >
    <div class="join-community__card">
      <!-- Header row -->
      <div class="join-community__header">
        <div>
          <h2 class="join-community__title">
            {{ label || $t('Views.Lobby.joinTheCommunity') }}
          </h2>
          <p class="join-community__subtitle">
            {{
              $tc(
                'Views.Lobby.JoinCommunity.connectWithProfessionals',
                rawCount,
                {
                  count: formattedCount
                }
              )
            }}
          </p>
        </div>
        <button
          class="join-community__btn"
          :aria-label="
            $tc('Views.Lobby.JoinCommunity.viewAllAttendeesAria', rawCount, {
              count: formattedCount
            })
          "
          @click="showParticipantsList"
        >
          {{ $t('Views.Lobby.JoinCommunity.viewAllAttendees') }}
        </button>
      </div>

      <!-- Avatar stack -->
      <div class="join-community__avatars">
        <div
          v-for="(user, index) in previewUsers"
          :key="user.UserID || index"
          class="avatar-ring"
          :style="{ zIndex: previewUsers.length - index }"
        >
          <Avatar
            :image="user.Picture"
            :circle="true"
            :size="'full'"
            :name="attendeeFullName(user)"
            :alt="user.Firstname || $t('Views.Lobby.JoinCommunity.attendee')"
            class="avatar-ring__img"
          />
        </div>
        <div v-if="overflow > 0" class="avatar-ring avatar-ring--count">
          +{{ overflow }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LobbyParticipants from '@/views/Stage/Participant/LobbyParticipants';
import Avatar from '@/components/shared/Avatar';
import { intlLocale } from '@/i18n';

export default {
  name: 'JoinCommunity',
  components: { Avatar },
  props: {
    // Section heading — driven by the admin's "Participants label".
    label: {
      type: String,
      default: ''
    }
  },
  created() {
    this.fetchUserTotalCount();
    this.fetchUsers({ page: 1, pageSize: 10, search: '', groupId: '' });
  },
  computed: {
    ...mapGetters('Users', {
      users: 'getUsers',
      totalCount: 'getUserTotalCount'
    }),
    ...mapGetters('CurrentEvent', ['participantVisibilityMode']),
    userList() {
      const u = this.users;
      if (Array.isArray(u)) return u;
      if (u && typeof u === 'object') return Object.values(u);
      return [];
    },
    previewUsers() {
      return this.userList.slice(0, 5);
    },
    overflow() {
      const count = this.totalCount || this.userList.length;
      return Math.max(0, count - 5);
    },
    rawCount() {
      return this.totalCount || this.userList.length || 0;
    },
    formattedCount() {
      const count = this.rawCount;
      if (!count) return '0';
      return count.toLocaleString(intlLocale());
    }
  },
  methods: {
    ...mapActions('Users', ['fetchUsers', 'fetchUserTotalCount']),
    attendeeFullName(user) {
      if (!user) return '';
      const first = user.Firstname || user.firstName || user.FirstName || '';
      const last = user.Lastname || user.lastName || user.LastName || '';
      return `${first} ${last}`.trim();
    },
    showParticipantsList() {
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        LobbyParticipants,
        {},
        {
          clickToClose: true,
          adaptive: true,
          minWidth: 250,
          maxWidth: 800,
          width: '80%',
          height: 'auto'
        },
        {
          opened: () => this.$root.$emit('focusChild'),
          closed: () => document.body.classList.remove('overflow-hidden')
        }
      );
    }
  }
};
</script>

<style scoped lang="scss">
// Match the unified vertical rhythm of the other lobby sections —
// 32 px desktop / 20 px mobile so the gap between every section is
// the same.
.join-community {
  padding-top: 32px;
  padding-bottom: 32px;

  @media (max-width: 640px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  &__card {
    background: #ffffff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;

    @media (max-width: 640px) {
      padding: 18px 16px;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 4px;
  }

  &__subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  // Match the "N speakers" pill in the Speakers section header — a
  // soft gray rounded chip rather than a filled brand-coloured CTA.
  &__btn {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    background: #f3f4f6;
    padding: 4px 12px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;
    font-family: 'Poppins', sans-serif;

    &:hover,
    &:focus-visible {
      background: #e5e7eb;
      color: #111827;
      outline: none;
    }
  }

  &__avatars {
    display: flex;
    align-items: center;
  }
}

.avatar-ring {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  overflow: hidden;
  margin-left: -10px;
  flex-shrink: 0;
  position: relative;

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    margin-left: -8px;
  }

  &:first-child {
    margin-left: 0;
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  // Avatar component fills the ring
  ::v-deep .avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &--count {
    background: var(--color-primary, #047873);
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
    border-radius: 50%;
  }
}
</style>
